import { type ForwardedRef, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Collapse, Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { TextInteractive } from '@pbt/pbt-ui-components'

import OrderListItem, {
  type OrderListItemProps,
} from '~/components/dashboard/soapV2/order/OrderListItem'
import { getPatientName } from '~/store/reducers/patients'
import type { Order } from '~/types'

import { addAnyPrefix } from './reminderProtocolUtils'
import ReminderWarning from './ReminderWarning'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    bundleNameText: {
      cursor: 'pointer',
    },
    caret: {
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
    collapse: {
      width: '100%',
      maxWidth: `calc(100% - ${theme.spacing(3)})`,
      paddingLeft: theme.spacing(3),
    },
  }),
  { name: 'ProtocolListItem' },
)

export interface ProtocolListItemProps extends OrderListItemProps {
  isSelected: boolean
  onUncheckItem: () => void
  selectedSubItem: Order
  updateSelectedItem: (order: Order) => void
  updateSelectedSubItem: () => void
}

const ProtocolListItem = forwardRef(function ProtocolListItem(
  {
    searchTerm,
    order: protocol,
    isSelected,
    isCheckedItem,
    onCheckItem,
    onUncheckItem,
    patientId,
    updateSelectedItem,
    ListItemProps,
    ...rest
  }: ProtocolListItemProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const classes = useStyles()
  const { t } = useTranslation(['MedicalHistory'])
  const patientName = useSelector(getPatientName(patientId))
  const hasCheckedItems = protocol.items?.some(isCheckedItem)
  const isProcedure = protocol.type === 'PROCEDURE'
  const shouldShowReminder = isProcedure && protocol.procedureWithReminder
  const anyOption = {
    id: protocol.id,
    type: protocol.type,
    name: addAnyPrefix(protocol.name),
  } as Order
  const everyOption = {
    ...anyOption,
    name: protocol.name,
  }
  const shouldShowAnyOption = protocol.items && protocol.items.length > 0
  const items = shouldShowAnyOption
    ? [anyOption].concat(protocol.items || [])
    : protocol.items

  const expand = () => {
    updateSelectedItem(protocol)
  }

  const mergeProtocolNameWithItemName = ({ name, ...orderFields }: Order) => ({
    name: `${protocol.name} | ${name}`,
    ...orderFields,
  })

  const handleCheckItem = (item: Order) => {
    if (item.id === protocol.id) {
      onCheckItem(item)
    } else {
      onCheckItem(mergeProtocolNameWithItemName(item))
    }
  }

  return (
    <Grid
      container
      item
      className={classes.root}
      direction="column"
      p={0.5}
      ref={ref}
    >
      <Grid
        container
        item
        alignItems="center"
        className={classes.bundleNameText}
        wrap="nowrap"
        onClick={expand}
      >
        {shouldShowAnyOption ? (
          <>
            {isSelected ? (
              <ExpandLess className={classes.caret} />
            ) : (
              <ExpandMore className={classes.caret} />
            )}
            <TextInteractive
              highlight={searchTerm}
              strong={hasCheckedItems}
              variant={hasCheckedItems ? 'h5' : 'body2'}
            >
              {protocol.name}
            </TextInteractive>
            {shouldShowReminder && (
              <ReminderWarning
                message={t(
                  'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.PATIENT_HAS_ACTIVE_REMINDER',
                  { patientName },
                )}
              />
            )}
          </>
        ) : (
          <Grid container item alignItems="center" wrap="nowrap">
            <OrderListItem
              ListItemProps={ListItemProps}
              isCheckedItem={isCheckedItem}
              isDisabled={shouldShowReminder}
              order={everyOption}
              patientId={patientId}
              searchTerm={searchTerm}
              onCheckItem={onCheckItem}
              onUncheckItem={onUncheckItem}
              {...rest}
            />
            {shouldShowReminder && (
              <ReminderWarning
                message={t(
                  'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.PATIENT_HAS_ACTIVE_REMINDER',
                  { patientName },
                )}
              />
            )}
          </Grid>
        )}
      </Grid>
      <Collapse
        mountOnEnter
        unmountOnExit
        className={classes.collapse}
        in={isSelected}
      >
        {items?.map((item, index) => (
          <OrderListItem
            {...rest}
            ListItemProps={ListItemProps}
            isCheckedItem={isCheckedItem}
            isDisabled={shouldShowReminder}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            order={item}
            patientId={patientId}
            searchTerm={searchTerm}
            onCheckItem={handleCheckItem}
            onUncheckItem={onUncheckItem}
          />
        ))}
      </Collapse>
    </Grid>
  )
})

export default ProtocolListItem
